/**
 * Model Analysis Effect for Sector-Model Template
 * Creates AI analysis sequence with node graph, processing, and stats
 */

export function startModelAnalysisEffect(containerEl) {
  if (!containerEl) return () => {};

  const timeouts = [];
  const intervals = [];

  // Find elements
  const modelHeader = containerEl.querySelector('.model-header');
  const analysisStatus = containerEl.querySelector('.analysis-status');
  const progressBar = containerEl.querySelector('.progress-bar-fill');
  const nodeGraphOverlay = containerEl.querySelector('.node-graph-overlay');
  const modelStats = containerEl.querySelector('.model-stats');
  const contentBlocks = containerEl.querySelectorAll('.model-content-block');

  // Sequence timing
  const INIT_START = 0;
  const PROGRESS_START = 400;
  const PROGRESS_DURATION = 2000;
  const GRAPH_START = 200;
  const STATS_START = 1800;
  const CONTENT_START = 2400;

  // 1. Initialization message
  if (analysisStatus) {
    timeouts.push(setTimeout(() => {
      analysisStatus.textContent = '> INITIALIZING MODEL...';
      analysisStatus.classList.add('visible');
    }, INIT_START));

    timeouts.push(setTimeout(() => {
      analysisStatus.textContent = '> ANALYZING SECTOR DATA...';
    }, 800));

    timeouts.push(setTimeout(() => {
      analysisStatus.textContent = '> PROCESSING 433 NODES, 1508 EDGES...';
    }, 1400));

    timeouts.push(setTimeout(() => {
      analysisStatus.textContent = '> ANALYSIS COMPLETE';
      analysisStatus.classList.add('complete');
    }, PROGRESS_START + PROGRESS_DURATION + 200));
  }

  // 2. Progress bar animation
  if (progressBar) {
    timeouts.push(setTimeout(() => {
      progressBar.style.transition = `width ${PROGRESS_DURATION}ms ease-out`;
      progressBar.style.width = '100%';
    }, PROGRESS_START));
  }

  // 3. Node graph overlay animation
  if (nodeGraphOverlay) {
    // Create and inject the SVG node graph
    const svg = createNodeGraphSVG();
    nodeGraphOverlay.appendChild(svg);
    
    timeouts.push(setTimeout(() => {
      nodeGraphOverlay.classList.add('visible');
      
      // Animate nodes lighting up
      const nodes = nodeGraphOverlay.querySelectorAll('.graph-node');
      nodes.forEach((node, index) => {
        timeouts.push(setTimeout(() => {
          node.classList.add('active');
        }, index * 100));
      });
    }, GRAPH_START));
  }

  // 4. Model header pulse
  if (modelHeader) {
    timeouts.push(setTimeout(() => {
      modelHeader.classList.add('analyzing');
    }, INIT_START));

    timeouts.push(setTimeout(() => {
      modelHeader.classList.remove('analyzing');
      modelHeader.classList.add('complete');
    }, PROGRESS_START + PROGRESS_DURATION));
  }

  // 5. Stats reveal
  if (modelStats) {
    timeouts.push(setTimeout(() => {
      modelStats.classList.add('visible');
    }, STATS_START));
  }

  // 6. Content blocks stagger reveal
  if (contentBlocks && contentBlocks.length > 0) {
    contentBlocks.forEach((block, index) => {
      timeouts.push(setTimeout(() => {
        block.classList.add('visible');
      }, CONTENT_START + (index * 200)));
    });
  }

  // 7. Continuous data stream effect
  if (nodeGraphOverlay) {
    const streamInterval = setInterval(() => {
      const nodes = nodeGraphOverlay.querySelectorAll('.graph-node');
      if (nodes.length > 0) {
        const randomNode = nodes[Math.floor(Math.random() * nodes.length)];
        randomNode.classList.add('pulse');
        setTimeout(() => {
          randomNode.classList.remove('pulse');
        }, 300);
      }
    }, 800);
    
    intervals.push(streamInterval);
  }

  // Cleanup
  return () => {
    timeouts.forEach((id) => clearTimeout(id));
    intervals.forEach((id) => clearInterval(id));
  };
}

/**
 * Helper to create simple node graph visualization
 */
export function createNodeGraphSVG() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('viewBox', '0 0 400 300');
  svg.style.opacity = '0.3';

  // Create random nodes
  const nodeCount = 20;
  const nodes = [];
  
  for (let i = 0; i < nodeCount; i++) {
    const x = 50 + Math.random() * 300;
    const y = 50 + Math.random() * 200;
    nodes.push({ x, y });
    
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', x);
    circle.setAttribute('cy', y);
    circle.setAttribute('r', '3');
    circle.setAttribute('fill', '#ffff00');
    circle.setAttribute('class', 'graph-node');
    svg.appendChild(circle);
  }

  // Create connections
  for (let i = 0; i < nodeCount; i++) {
    const connections = Math.floor(Math.random() * 3) + 1;
    for (let j = 0; j < connections; j++) {
      const targetIndex = Math.floor(Math.random() * nodeCount);
      if (targetIndex !== i) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', nodes[i].x);
        line.setAttribute('y1', nodes[i].y);
        line.setAttribute('x2', nodes[targetIndex].x);
        line.setAttribute('y2', nodes[targetIndex].y);
        line.setAttribute('stroke', '#ffff00');
        line.setAttribute('stroke-width', '0.5');
        line.setAttribute('opacity', '0.2');
        svg.insertBefore(line, svg.firstChild);
      }
    }
  }

  return svg;
}
